"use client";

import { useState, useRef, useCallback } from "react";
import NextImage from "next/image";
import { ArrowLeftRight } from "lucide-react";

export interface BeforeAfterItem {
  /** Short caption under the slider. */
  caption: string;
  /** Public URL for the "before" image. */
  before_src: string;
  before_alt?: string;
  before_focal_x?: number;
  before_focal_y?: number;
  /** Public URL for the "after" image. */
  after_src: string;
  after_alt?: string;
  after_focal_x?: number;
  after_focal_y?: number;
}

interface Props {
  items: BeforeAfterItem[];
  title?: string;
  subtitle?: string;
}

/**
 * Lightweight before/after slider gallery — native range input for keyboard
 * accessibility + a draggable handle. No external dependency.
 *
 * Each card maintains its own handle position so they slide independently.
 */
export default function BeforeAfter({ items, title, subtitle }: Props) {
  if (items.length === 0) return null;
  return (
    <section className="py-14 md:py-20 bg-cream-100">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-10 md:mb-14">
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight tracking-tight">
            {title ?? "Voor en na"}
          </h2>
          {subtitle ? (
            <p className="mt-3 text-lg text-stone-700 leading-relaxed">{subtitle}</p>
          ) : null}
        </div>

        <div className={`grid gap-6 ${items.length === 1 ? "" : "md:grid-cols-2"}`}>
          {items.map((item, i) => (
            <BeforeAfterCard key={i} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterCard({ item }: { item: BeforeAfterItem }) {
  const [pos, setPos] = useState<number>(50);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef<boolean>(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <figure className="overflow-hidden rounded-card bg-white shadow-card">
      <div
        ref={ref}
        className="relative aspect-[4/3] select-none touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {/* After (base layer) */}
        <NextImage
          src={item.after_src}
          alt={item.after_alt ?? "Na renovatie"}
          fill
          loading="lazy"
          sizes="(max-width: 768px) 100vw, 50vw"
          quality={80}
          className="object-cover"
          style={{ objectPosition: `${item.after_focal_x ?? 50}% ${item.after_focal_y ?? 50}%` }}
        />

        {/* Before (clipped to the left of the slider) */}
        <div
          aria-hidden
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${pos}%` }}
        >
          <NextImage
            src={item.before_src}
            alt={item.before_alt ?? "Voor renovatie"}
            width={1600}
            height={1066}
            loading="lazy"
            sizes="(max-width: 768px) 100vw, 50vw"
            quality={80}
            className="h-full object-cover"
            style={{
              width: `${(100 / pos) * 100}%`,
              maxWidth: "none",
              objectPosition: `${item.before_focal_x ?? 50}% ${item.before_focal_y ?? 50}%`,
            }}
          />
        </div>

        {/* Labels */}
        <span className="pointer-events-none absolute top-3 left-3 rounded-pill bg-ink/85 text-white px-2.5 py-1 text-xs font-semibold uppercase tracking-wider">
          Voor
        </span>
        <span className="pointer-events-none absolute top-3 right-3 rounded-pill bg-terracotta text-white px-2.5 py-1 text-xs font-semibold uppercase tracking-wider">
          Na
        </span>

        {/* Handle */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <div className="h-full w-[3px] bg-white shadow-[0_0_0_1px_rgba(30,42,58,0.2)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-card-hover ring-2 ring-ink/10 text-terracotta">
            <ArrowLeftRight size={18} strokeWidth={2.25} />
          </div>
        </div>

        {/* Accessible slider — positioned over the image for keyboard users */}
        <input
          type="range"
          aria-label="Schuif om voor/na te vergelijken"
          min={0}
          max={100}
          value={Math.round(pos)}
          onChange={(e) => setPos(Number(e.target.value))}
          className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0 focus-visible:opacity-100 focus-visible:outline-none"
        />
      </div>
      {item.caption ? (
        <figcaption className="px-5 md:px-6 py-4 text-sm text-stone-700 leading-relaxed">
          {item.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
